/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.ui

import io.peekandpoke.klang.audio_bridge.KlangPlaybackSignal
import io.peekandpoke.klang.common.SourceLocation
import io.peekandpoke.klang.script.types.KlangSymbol
import io.peekandpoke.ultra.common.TypedAttributes
import io.peekandpoke.ultra.common.TypedKey
import io.peekandpoke.ultra.semanticui.SemanticIconFn
import io.peekandpoke.ultra.streams.Stream
import kotlinx.html.FlowContent

/**
 * Context passed to a [KlangUiTool] when it is opened.
 *
 * @param symbol       The function whose param triggered the tool.
 * @param paramName    The name of the param that has this tool attached.
 * @param currentValue Raw source text of the argument at the cursor (null if no arg is present yet).
 * @param onCommit     Call with the replacement source text to commit the edit back to the editor.
 * @param onCancel     Call to dismiss without making any change.
 * @param attrs        Extensible typed attributes for passing module-specific data (e.g. playback signals).
 */
data class KlangUiToolContext(
    val symbol: KlangSymbol,
    val paramName: String,
    val currentValue: String?,
    val onCommit: (String) -> Unit,
    val onCancel: () -> Unit,
    val attrs: TypedAttributes = TypedAttributes.empty,
    val call: KlangUiToolCall? = null,
) {
    companion object {
        /** Typed key for the raw playback signal stream. UI components filter what they need. */
        val PlaybackVoiceEvents = TypedKey<Stream<KlangPlaybackSignal>>("PlaybackVoices")

        /** Typed key for the base source location of the edited string literal (opening-quote position). */
        val BaseSourceLocation = TypedKey<SourceLocation>("BaseSourceLocation")
    }
}


/**
 * Whole-call view for tools that edit several parameters of the SAME call at once
 * (the MultiParam tier of the two-tool-tier design, see KLANG-00227 (filter unification) C0).
 *
 * Present on [KlangUiToolContext.call] when the editor could resolve the host call's full
 * argument list. Scalar tools ignore it; compound tools (filter, adsr, compressor, ...) read
 * their sibling parameters from [args] and commit the whole argument list at once.
 *
 * @param paramNames   Declared user-facing parameter names, in declaration order.
 * @param args         Current raw source text per parameter index; null = not provided.
 * @param onCommitCall Commit the ENTIRE argument list. Pass the new per-param texts
 *                     (null = omit). The editor serializes a contiguous prefix as positional
 *                     args, anything with gaps as all-named args (KlangScript forbids mixing).
 */
data class KlangUiToolCall(
    val paramNames: List<String>,
    val args: List<String?>,
    val onCommitCall: (List<String?>) -> Unit,
)

/**
 * A UI tool that can edit a function argument interactively.
 *
 * Implement this interface and register it with [KlangUiToolRegistry] under the name
 * declared in a `@tool` KDoc tag.
 */
fun interface KlangUiTool {
    val title: String? get() = null

    val iconFn: SemanticIconFn get() = { wrench }

    /**
     * True for a tool that edits a whole mini-notation sequence, usually by wrapping a value tool.
     * The Sprudel tab opens on it for a string argument and on the value tool for anything else
     * (see [defaultTool]); the tab's keys offer both.
     */
    val editsSequence: Boolean get() = false

    fun FlowContent.render(ctx: KlangUiToolContext)
}

/**
 * The tool the Sprudel tab opens on for an argument whose trimmed source text is [argText]
 * (KLANG-00374): the sequence tool for a string literal, a value tool (the call's own, listed
 * first, when it has one) for anything else, and the first tool when no tool of that kind is
 * declared. Null for an empty list. A param declares its value tool AND the sequence tool that
 * wraps it (`@tool wet: SprudelReverbEditor, SprudelReverbSequenceEditor`); the other is a key away.
 */
fun List<Pair<String, KlangUiTool>>.defaultTool(argText: String): Pair<String, KlangUiTool>? {
    val isStringLiteral = argText.startsWith("\"") || argText.startsWith("'") || argText.startsWith("`")

    return firstOrNull { (_, tool) -> tool.editsSequence == isStringLiteral } ?: firstOrNull()
}

/**
 * A [KlangUiTool] that can also render its editing content inline, without
 * Cancel / Reset / Update buttons.
 *
 * When rendered embedded, [FlowContent.renderEmbedded] is called instead of [FlowContent.render].
 * The tool must call [KlangUiToolContext.onCommit] on every live change so that the host
 * (e.g. the mini-notation editor) stays in sync automatically.
 */
interface KlangUiToolEmbeddable : KlangUiTool {
    fun FlowContent.renderEmbedded(ctx: KlangUiToolContext)
}

/**
 * Global registry mapping tool names (as declared in `@tool` KDoc tags) to [KlangUiTool] implementations.
 *
 * Register tools at application startup:
 * ```kotlin
 * KlangUiToolRegistry.register("StrudelAdsrEditor", StrudelAdsrEditor())
 * ```
 */
object KlangUiToolRegistry {
    private val tools = mutableMapOf<String, KlangUiTool>()

    fun register(name: String, tool: KlangUiTool) {
        tools[name] = tool
    }

    fun get(name: String): KlangUiTool? = tools[name]

    /** Returns all tool names registered for the given list of names (preserving order, skipping unknowns). */
    fun resolve(names: List<String>): List<Pair<String, KlangUiTool>> =
        names.mapNotNull { name -> tools[name]?.let { name to it } }
}
